import { Box, Divider, Typography } from "@mui/material";
import pluralize from "pluralize";
import { useContext, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import CustomToolTip from "../../../../components/CustomTooltip";
import { EnvFeatureFlagContext } from "../../../../contexts/EnvFeaturesContext";
import { formatNumberCompact } from "../../../../utils/formatters";
import OverviewGauge from "../../environment/components/EntityTypeGauge/OverviewGauge";
import AssetsOverviewActions from "./AssetsOverviewActions";
import AddNewAssetButton from "./cards/AddNewAssetSectionCard";

const AssetsOverview = ({
  allCards,
  setSearchParams,
  overviewData,
  handleCreateAssetClick,
  fetchedEntitiesAreLoading,
  hasPermission,
  displayMode,
  setDisplayMode,
  appliedFilters,
  setAppliedFilters,
  entityOptions = [],
  entitiesAreLoading = false,
}) => {
  const colors = useColors();
  const { businessValueMode } = useContext(EnvFeatureFlagContext);
  const [searchValue, setSearchValue] = useState("");

  const totalBusinessValue = overviewData?.total_business_value || 0;

  const businessValueInfo = [
    {
      quantity: formatNumberCompact(totalBusinessValue),
      title: "Business Value",
      prefix: "$",
      pluralize: "Business Value",
    },
    {
      quantity: formatNumberCompact(overviewData?.value_at_risk || 0),
      title: "Value at Risk",
      prefix: "$",
      pluralize: "Value at Risk",
    },
  ];

  const assetsInfo = [
    { quantity: overviewData?.assets?.length || 0, title: "Critical Asset" },
    ...(businessValueMode ? businessValueInfo : []),
  ];

  const pathsInfo = [
    {
      quantity: overviewData?.attack_scenarios || 0,
      title: "Attack Scenarios",
    },
    {
      quantity: overviewData?.potential_attack_scenarios || 0,
      title: "Potential Scenarios",
    },
    {
      quantity: overviewData?.mitigated_attack_scenarios || 0,
      title: "Mitigated Scenarios",
    },
  ];

  const adjustTitle = (item) => {
    if (item.quantity === 1) return item.title;
    if (item?.pluralize) return item.pluralize;
    return pluralize(item.title, item.quantity);
  };

  return (
    <Box
      width="100%"
      padding="16px"
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      gap={2}
      sx={{
        borderRadius: "12px",
        backgroundColor: colors.grey_2percent,
        boxShadow: colors.entity_type_overview_top_shadow,
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Box height="75px" display="flex" gap={4}>
          <Box minWidth="131px" paddingRight={2} position="relative">
            <Box display="flex" justifyContent="flex-end">
              <Typography variant="subtitle1">Coverage</Typography>
            </Box>
            <Box position="relative" left="38px" bottom="5px">
              <OverviewGauge
                value={overviewData?.coverage || 0}
                tooltipText="Overall Coverage of Critical Assets"
              />
            </Box>
          </Box>
          <Divider
            orientation="vertical"
            sx={{ borderColor: colors.divider }}
          />
          <Box display="flex" gap={4} alignItems="center">
            {assetsInfo.map((item, index) => (
              <Box key={index} position="relative">
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={0.5}
                  paddingY={1}
                >
                  <Typography variant="h6">
                    {item.prefix
                      ? `${item.prefix}${item.quantity}`
                      : item.quantity}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={colors.text_primary}
                    noWrap
                  >
                    {adjustTitle(item)}
                  </Typography>
                </Box>
                {item.tooltipTitle && (
                  <CustomToolTip title={item.tooltipTitle} placement="top">
                    <Box
                      position="absolute"
                      width="90%"
                      height="55%"
                      top="18px"
                      left="0"
                    ></Box>
                  </CustomToolTip>
                )}
              </Box>
            ))}
          </Box>
          <Divider
            orientation="vertical"
            sx={{ borderColor: colors.divider }}
          />
          <Box display="flex" gap={4} alignItems="center">
            {pathsInfo.map((item, index) => (
              <Box key={index} position="relative">
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={0.5}
                  paddingY={1}
                >
                  <Typography variant="h6">{item.quantity}</Typography>
                  <Typography
                    variant="body2"
                    color={colors.text_primary}
                    noWrap
                  >
                    {adjustTitle(item)}
                  </Typography>
                </Box>
                {item.tooltipTitle && (
                  <CustomToolTip title={item.tooltipTitle} placement="top">
                    <Box
                      position="absolute"
                      width="90%"
                      height="55%"
                      top="18px"
                      left="0"
                    ></Box>
                  </CustomToolTip>
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <AddNewAssetButton
          onClick={handleCreateAssetClick}
          isLoading={fetchedEntitiesAreLoading}
          hasPermission={hasPermission}
        />
      </Box>
      <Divider />
      <AssetsOverviewActions
        entityOptions={entityOptions}
        entitiesAreLoading={entitiesAreLoading}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        displayMode={displayMode}
        setDisplayMode={setDisplayMode}
        setSearchParams={setSearchParams}
        allCards={allCards}
      />
    </Box>
  );
};

export default AssetsOverview;
